import { useState, useEffect } from "react";
import { getYtdReport } from "../services/analyticsService";

/**
 * @param {number} year
 * @param {number} month 1–12
 * @param {number|null} annualBudget signed total from report settings (may be negative for expense-style entry)
 * @param {string} [glCode] optional GL filter; "" = all codes
 */
export const useYtdReport = (year, month, annualBudget, glCode = "") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const glKey = glCode != null ? String(glCode).trim() : "";

  useEffect(() => {
    if (!year || annualBudget == null || Number.isNaN(Number(annualBudget))) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await getYtdReport(year, month, annualBudget, glKey);
        if (cancelled) return;
        if (response?.success && response?.data) {
          setData(response.data);
        } else {
          setData(null);
          setError(new Error(response?.error || "YTD report failed"));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [year, month, annualBudget, glKey]);

  const refresh = async () => {
    if (!year || annualBudget == null || Number.isNaN(Number(annualBudget))) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await getYtdReport(year, month, annualBudget, glKey);
      if (response?.success && response?.data) {
        setData(response.data);
      } else {
        setData(null);
        setError(new Error(response?.error || "YTD report failed"));
      }
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh };
};
